import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../App';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { History, CheckCircle, ExternalLink, Pencil, Radar, Globe, Crosshair, Mail, FileText, User, ChevronRight, ArrowDown, ArrowUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const SOURCE_LABELS = {
  creer: { label: 'Creation', icon: Pencil, color: 'text-sky-400 bg-sky-500/15' },
  creer_extra: { label: 'Creation', icon: Pencil, color: 'text-sky-400 bg-sky-500/15' },
  radar: { label: 'Radar', icon: Radar, color: 'text-amber-400 bg-amber-500/15' },
  sonar: { label: 'Sonar', icon: Globe, color: 'text-purple-400 bg-purple-500/15' },
  cible: { label: 'Cible', icon: Crosshair, color: 'text-rose-400 bg-rose-500/15' },
  tribune: { label: 'Tribune', icon: Mail, color: 'text-emerald-400 bg-emerald-500/15' },
};

const SourceBadge = ({ source }) => {
  const config = SOURCE_LABELS[source] || { label: source || 'Inconnu', icon: FileText, color: 'text-slate-400 bg-slate-500/15' };
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-mono px-2 py-0.5 rounded ${config.color}`} data-testid={`source-badge-${source}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    published: { label: 'En ligne', cls: 'bg-green-500/15 text-green-400 border-green-500/30' },
    draft:     { label: 'Brouillon', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
    generated: { label: 'Non publié', cls: 'bg-slate-500/15 text-slate-400 border-slate-500/30' },
  };
  const m = map[status] || map.generated;
  return (
    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${m.cls}`}>
      {m.label}
    </span>
  );
};

const Publications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [sites, setSites] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterSource, setFilterSource] = useState(null);
  const [expanded, setExpanded] = useState({});
  // Tri : "date_desc" (par défaut), "date_asc", "author_asc", "author_desc"
  const [sortKey, setSortKey] = useState('date_desc');

  const loadData = useCallback(async () => {
    try {
      const [groupsRes, sitesRes] = await Promise.all([
        axios.get(`${API}/publications/grouped`),
        axios.get(`${API}/wordpress-sites`),
      ]);
      setGroups(groupsRes.data || []);
      const sitesMap = {};
      (sitesRes.data || []).forEach(s => sitesMap[s.id] = s);
      setSites(sitesMap);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toggle = (gid) => setExpanded(prev => ({ ...prev, [gid]: !prev[gid] }));

  const sourceCounts = groups.reduce((acc, g) => {
    const src = g.source || 'creer';
    const key = src === 'creer_extra' ? 'creer' : src;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const totalArticles = groups.reduce((s, g) => s + (g.articles_count || 0), 0);

  const filtered = filterSource
    ? groups.filter(g => {
        const src = (g.source || 'creer') === 'creer_extra' ? 'creer' : (g.source || 'creer');
        return src === filterSource;
      })
    : groups;

  // Tri appliqué (immutable : on clone avant sort)
  const sorted = [...filtered].sort((a, b) => {
    if (sortKey === 'date_desc') {
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    }
    if (sortKey === 'date_asc') {
      return new Date(a.created_at || 0) - new Date(b.created_at || 0);
    }
    if (sortKey === 'author_asc' || sortKey === 'author_desc') {
      const an = (a.author_name || '').toLowerCase();
      const bn = (b.author_name || '').toLowerCase();
      // Mettre les sans-auteur en dernier
      if (!an && bn) return 1;
      if (an && !bn) return -1;
      if (an === bn) {
        // secondary sort by date desc
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      return sortKey === 'author_asc' ? an.localeCompare(bn, 'fr') : bn.localeCompare(an, 'fr');
    }
    return 0;
  });

  // Décompte du jour (jour civil Europe/Paris, depuis 00h00 — non glissant)
  // Pour chaque auteur : nb de créations (groupes) + nb d'articles générés
  const todayStartUtc = (() => {
    // Calcul robuste du début du jour Paris en UTC
    const now = new Date();
    
    // Créer une date en heure Paris puis récupérer minuit
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Paris',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(now);
    const getPart = (type) => parts.find(p => p.type === type)?.value || '0';
    
    const parisHour = parseInt(getPart('hour'), 10);
    const parisMinute = parseInt(getPart('minute'), 10);
    const parisSecond = parseInt(getPart('second'), 10);
    
    // Millisecondes depuis minuit Paris
    const msSinceParisMidnight = ((parisHour * 60 + parisMinute) * 60 + parisSecond) * 1000;
    
    // now - temps depuis minuit = minuit Paris en UTC
    return new Date(now.getTime() - msSinceParisMidnight);
  })();
  const yesterdayStartUtc = new Date(todayStartUtc.getTime() - 86400000);

  const aggregateByAuthor = (fromUtc, toUtc) => {
    const acc = {};
    groups.forEach(g => {
      if (!g.created_at) return;
      const d = new Date(g.created_at);
      if (d < fromUtc || d >= toUtc) return;
      const name = g.author_name || '— (sans auteur)';
      if (!acc[name]) acc[name] = { creations: 0, articles: 0 };
      acc[name].creations += 1;
      acc[name].articles += (g.articles_count || 0);
    });
    return Object.entries(acc).sort((a, b) => b[1].articles - a[1].articles);
  };

  const todayByAuthor = aggregateByAuthor(todayStartUtc, new Date(8640000000000000));
  const yesterdayByAuthor = aggregateByAuthor(yesterdayStartUtc, todayStartUtc);
  const yesterdayLabel = new Date(yesterdayStartUtc.getTime() + 12 * 3600 * 1000)
    .toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris', weekday: 'long', day: '2-digit', month: 'long' });

  const getFaviconUrl = (url) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    } catch { return null; }
  };

  return (
    <div className="min-h-screen p-6" data-testid="publications-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold tracking-wider uppercase"
            style={{ fontFamily: "'Orbitron', monospace", color: '#00e5ff', textShadow: '0 0 20px rgba(0,229,255,0.3)' }}>
            Historique des publications
          </h1>
          <p className="text-slate-500 font-mono text-sm mt-1">
            {groups.length} création{groups.length !== 1 ? 's' : ''} · {totalArticles} article{totalArticles !== 1 ? 's' : ''} généré{totalArticles !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Décompte du jour par rédacteur (depuis 00h00 Paris, non glissant) */}
        {!loading && (() => {
          const blocks = [
            {
              key: 'today',
              label: `Aujourd'hui (${new Date().toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris', weekday: 'long', day: '2-digit', month: 'long' })})`,
              hint: 'depuis 00h00 Paris',
              data: todayByAuthor,
              emptyMsg: "Aucune création aujourd'hui",
              accent: 'indigo',
            },
            {
              key: 'yesterday',
              label: `Hier (${yesterdayLabel})`,
              hint: '00h00 → 23h59 Paris',
              data: yesterdayByAuthor,
              emptyMsg: 'Aucune création hier',
              accent: 'slate',
            },
          ];
          return (
            <div className="space-y-3 mb-6">
              {blocks.map(b => {
                const isIndigo = b.accent === 'indigo';
                return (
                  <Card key={b.key}
                    className={isIndigo ? 'bg-indigo-500/5 border-indigo-500/30' : 'bg-slate-800/30 border-slate-700'}
                    data-testid={`${b.key}-by-author`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <User className={`w-4 h-4 ${isIndigo ? 'text-indigo-300' : 'text-slate-400'}`} />
                        <span className={`text-xs font-mono uppercase tracking-wider ${isIndigo ? 'text-indigo-300' : 'text-slate-400'}`}>
                          {b.label}
                        </span>
                        <span className="text-[10px] text-slate-500 font-mono">— {b.hint}</span>
                      </div>
                      {b.data.length === 0 ? (
                        <p className="text-xs text-slate-500 font-mono italic">{b.emptyMsg}</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {b.data.map(([name, stats]) => (
                            <div
                              key={name}
                              className="flex items-center gap-2 px-3 py-1.5 rounded bg-slate-900/60 border border-slate-700"
                              data-testid={`${b.key}-author-${name}`}
                            >
                              <span className="text-xs text-slate-200 font-medium">{name}</span>
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300">
                                {stats.creations} création{stats.creations > 1 ? 's' : ''}
                              </span>
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-300">
                                {stats.articles} article{stats.articles > 1 ? 's' : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          );
        })()}

        {/* Filtres par source */}
        {groups.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="source-filters">
            <button
              onClick={() => setFilterSource(null)}
              className={`text-xs font-mono px-3 py-1.5 rounded border transition-all ${!filterSource ? 'bg-sky-500/20 border-sky-500 text-sky-300' : 'bg-slate-900/50 border-slate-700 text-slate-500 hover:border-slate-500'}`}
              data-testid="filter-all">
              Toutes ({groups.length})
            </button>
            {Object.entries(SOURCE_LABELS)
              .filter(([key, _]) => key !== 'creer_extra' && (sourceCounts[key] || 0) > 0)
              .map(([key, cfg]) => {
              const count = sourceCounts[key] || 0;
              const Icon = cfg.icon;
              return (
                <button key={key}
                  onClick={() => setFilterSource(filterSource === key ? null : key)}
                  className={`text-xs font-mono px-3 py-1.5 rounded border transition-all flex items-center gap-1.5 ${filterSource === key ? 'bg-sky-500/20 border-sky-500 text-sky-300' : 'bg-slate-900/50 border-slate-700 text-slate-500 hover:border-slate-500'}`}
                  data-testid={`filter-${key}`}>
                  <Icon className="w-3 h-3" />
                  {cfg.label} ({count})
                </button>
              );
            })}
          </div>
        )}

        {/* Tri */}
        {groups.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="sort-controls">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Trier :</span>
            {[
              { key: 'date_desc',   label: 'Date',     icon: ArrowDown, hint: 'plus récent' },
              { key: 'date_asc',    label: 'Date',     icon: ArrowUp,   hint: 'plus ancien' },
              { key: 'author_asc',  label: 'Auteur',   icon: ArrowDown, hint: 'A → Z' },
              { key: 'author_desc', label: 'Auteur',   icon: ArrowUp,   hint: 'Z → A' },
            ].map(({ key, label, icon: Icon, hint }) => (
              <button
                key={key}
                onClick={() => setSortKey(key)}
                className={`text-xs font-mono px-3 py-1.5 rounded border transition-all flex items-center gap-1.5 ${sortKey === key ? 'bg-indigo-500/20 border-indigo-500 text-indigo-300' : 'bg-slate-900/50 border-slate-700 text-slate-500 hover:border-slate-500'}`}
                data-testid={`sort-${key}`}
                title={`${label} (${hint})`}
              >
                <Icon className="w-3 h-3" />
                {label}
                <span className="text-[9px] text-slate-500">{hint}</span>
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="text-slate-500 font-mono text-center py-12">Chargement...</p>
        ) : sorted.length === 0 ? (
          <Card className="bg-slate-900/30 border-slate-800">
            <CardContent className="py-12 text-center">
              <History className="w-12 h-12 mx-auto mb-3 text-slate-700" />
              <p className="text-slate-500 font-mono text-sm">Aucune création</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {sorted.map((g) => {
              const isOpen = !!expanded[g.group_id];
              const src = (g.source || 'creer') === 'creer_extra' ? 'creer' : (g.source || 'creer');
              return (
                <Card key={g.group_id} data-testid={`group-${g.group_id}`}
                  className="bg-slate-900/30 border-slate-800 hover:border-slate-600 transition-all">
                  <CardContent className="p-4">
                    {/* En-tête du groupe : cliquable pour déplier */}
                    <button
                      type="button"
                      onClick={() => toggle(g.group_id)}
                      className="w-full text-left"
                      data-testid={`group-toggle-${g.group_id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            <SourceBadge source={src} />
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-300 border border-sky-500/20">
                              {g.articles_count} article{g.articles_count !== 1 ? 's' : ''}
                            </span>
                            {g.published_count > 0 && (
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-green-500/15 text-green-400">
                                {g.published_count} en ligne
                              </span>
                            )}
                            {g.draft_count > 0 && (
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">
                                {g.draft_count} brouillon{g.draft_count !== 1 ? 's' : ''}
                              </span>
                            )}
                            {g.generated_count > 0 && (
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-400">
                                {g.generated_count} non publié{g.generated_count !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <h3 className="text-sm font-medium text-slate-200 truncate" data-testid={`group-title-${g.group_id}`}>
                            {g.first_title || 'Sans titre'}
                          </h3>
                          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-600 font-mono flex-wrap">
                            <span>{new Date(g.created_at).toLocaleString('fr-FR')}</span>
                            {g.author_name && (
                              <span className="inline-flex items-center gap-1 text-indigo-400">
                                <User className="w-3 h-3" />{g.author_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className={`w-5 h-5 text-slate-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                      </div>
                    </button>

                    {/* Liste des articles du groupe */}
                    {isOpen && (
                      <div className="mt-4 pt-3 border-t border-slate-800 space-y-2" data-testid={`group-articles-${g.group_id}`}>
                        {g.articles.map((a) => {
                          const site = sites[a.site_id];
                          const favicon = site ? getFaviconUrl(site.url) : null;
                          return (
                            <div key={a.article_id}
                              className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-950/40 border border-slate-800/60 hover:border-sky-500/40 transition-colors"
                              data-testid={`group-article-${a.article_id}`}
                            >
                              {favicon && (
                                <img src={favicon} alt="" className="w-4 h-4 rounded shrink-0"
                                  onError={(e) => e.target.style.display = 'none'} />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs text-slate-300 font-semibold truncate">
                                    {a.site_name || a.extra_label || '—'}
                                  </span>
                                  <StatusPill status={a.status} />
                                  {a.wordpress_post_id && (
                                    <span className="text-[10px] text-slate-600 font-mono">WP #{a.wordpress_post_id}</span>
                                  )}
                                </div>
                                <p className="text-[11px] text-slate-500 mt-0.5 truncate" title={a.title}>
                                  {a.title || 'Sans titre'}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {a.wordpress_url && (
                                  <a
                                    href={a.wordpress_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1.5 hover:bg-slate-800 rounded transition-colors"
                                    title="Ouvrir sur WordPress"
                                    data-testid={`open-wp-${a.article_id}`}
                                  >
                                    <ExternalLink className="w-4 h-4 text-emerald-400" />
                                  </a>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(`/article/${a.article_id}`); }}
                                  className="p-1.5 hover:bg-slate-700 rounded transition-colors"
                                  title="Voir l'article"
                                  data-testid={`open-article-${a.article_id}`}
                                >
                                  <CheckCircle className="w-4 h-4 text-sky-400" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Publications;
